/**
 * Comprehensive HTML sanitization to prevent XSS attacks
 * Based on OWASP XSS Prevention guidelines and common attack vectors
 */

/**
 * Sanitizes HTML string to prevent XSS attacks by removing dangerous tags,
 * attributes, and protocols
 * @param HTML - The HTML string to sanitize
 * @returns Sanitized HTML string
 */
const sanitizeHTML = (HTML: string = ''): string => {
  if (!HTML || typeof HTML !== 'string') {
    return '';
  }

  // Normalize whitespace: collapse newlines and multiple spaces
  // This helps regex patterns match across line breaks
  let sanitized = HTML.replace(/\r\n|\r|\n/g, ' ').replace(/\s+/g, ' ');

  // 1. Remove all script tags (with any attributes, handles all variations)
  // Using multiple passes to catch edge cases
  sanitized = sanitized.replace(/<script\b[^>]*>.*?<\/script>/gi, '');
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // 2. Remove script tags without closing tags
  sanitized = sanitized.replace(/<script\b[^>]*>/gi, '');
  
  // 3. Remove any remaining script closing tags
  sanitized = sanitized.replace(/<\/script>/gi, '');
  
  // 4. Remove iframe tags
  sanitized = sanitized.replace(/<iframe\b[^>]*>.*?<\/iframe>/gi, '');
  sanitized = sanitized.replace(/<iframe\b[^>]*>/gi, '');
  sanitized = sanitized.replace(/<\/iframe>/gi, '');
  
  // 5. Remove object and embed tags
  sanitized = sanitized.replace(/<object\b[^>]*>.*?<\/object>/gi, '');
  sanitized = sanitized.replace(/<embed\b[^>]*>.*?<\/embed>/gi, '');
  sanitized = sanitized.replace(/<object\b[^>]*>/gi, '');
  sanitized = sanitized.replace(/<embed\b[^>]*>/gi, '');
  
  // 6. Remove style tags with expressions
  sanitized = sanitized.replace(/<style\b[^>]*>.*?<\/style>/gi, '');
  sanitized = sanitized.replace(/<link\b[^>]*>/gi, '');
  
  // 7. Remove meta tags (can be used for refresh redirects)
  sanitized = sanitized.replace(/<meta\b[^>]*>/gi, '');
  
  // 8. Remove base tags
  sanitized = sanitized.replace(/<base\b[^>]*>/gi, '');
  
  // 9. Remove SVG tags (can contain scripts)
  sanitized = sanitized.replace(/<svg\b[^>]*>.*?<\/svg>/gi, '');
  
  // 10. Remove all event handler attributes (on*)
  // Handle double quotes: onclick="..."
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*"[^"]*"/gi, '');
  // Handle single quotes: onclick='...'
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*'[^']*'/gi, '');
  // Handle without quotes: onclick=something
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
  
  // 11. Remove srcset attribute entirely if it contains javascript (BEFORE removing protocol)
  sanitized = sanitized.replace(/\s*srcset\s*=\s*["'][^"']*["']/gi, (match) => {
    if (/javascript:/i.test(match)) {
      return '';
    }
    return match;
  });
  
  // 12. Remove formaction attribute entirely if it contains javascript (BEFORE removing protocol)
  sanitized = sanitized.replace(/\s*formaction\s*=\s*["'][^"']*["']/gi, (match) => {
    if (/javascript:/i.test(match)) {
      return '';
    }
    return match;
  });
  
  // 13. Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // 14. Remove vbscript: protocol
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // 15. Remove data: protocol (can contain base64 encoded scripts)
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  
  // 16. Remove expression() in styles (handles nested parentheses)
  // This regex handles one level of nested parentheses like expression(alert('XSS'))
  sanitized = sanitized.replace(/expression\s*\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, '');
  
  // 17. Remove import in styles
  sanitized = sanitized.replace(/@import/gi, '');
  
  // 18. Remove HTML comments that might contain scripts
  sanitized = sanitized.replace(/<!--.*?-->/g, '');
  
  // 19. Remove CDATA sections
  sanitized = sanitized.replace(/<!\[CDATA\[.*?\]\]>/gi, '');
  
  // 20. Remove XML processing instructions
  sanitized = sanitized.replace(/<\?.*?\?>/gi, '');
  
  // 21. Remove form tags
  sanitized = sanitized.replace(/<form\b[^>]*>.*?<\/form>/gi, '');
  sanitized = sanitized.replace(/<form\b[^>]*>/gi, '');
  
  // 22. Remove input tags
  sanitized = sanitized.replace(/<input\b[^>]*>/gi, '');
  
  // 23. Remove button tags with potential XSS (especially with event handlers)
  sanitized = sanitized.replace(/<button\b[^>]*>.*?<\/button>/gi, '');
  sanitized = sanitized.replace(/<button\b[^>]*>/gi, '');
  
  // 24. Handle encoded script tags
  // Remove encoded opening and closing tags (but keep content between them)
  sanitized = sanitized.replace(/&lt;script\b[^&]*&gt;/gi, '');
  sanitized = sanitized.replace(/&lt;\/script&gt;/gi, '');
  sanitized = sanitized.replace(/&lt;iframe\b[^&]*&gt;/gi, '');
  sanitized = sanitized.replace(/&lt;\/iframe&gt;/gi, '');
  
  // 25. Clean up any remaining malformed tags and partial tags
  // Remove any remaining script-related text
  sanitized = sanitized.replace(/<script/gi, '');
  sanitized = sanitized.replace(/<\/script>/gi, '');
  sanitized = sanitized.replace(/script>/gi, '');
  
  // Clean up any remaining iframe fragments
  sanitized = sanitized.replace(/<iframe/gi, '');
  sanitized = sanitized.replace(/<\/iframe>/gi, '');
  sanitized = sanitized.replace(/iframe>/gi, '');
  
  // Final cleanup: normalize multiple spaces again (from removed tags) and trim
  sanitized = sanitized.replace(/\s+/g, ' ').trim();

  return sanitized;
};

export default sanitizeHTML;
